import { getSafeUrl } from "@/src/utils/safeNavigation";
import warnLeavingUnsaved from "@/src/utils/warnLeavingUnsaved";

/**
 * Recovery from lazy chunks that no longer exist on the server.
 *
 * A deploy replaces the hashed files in `public/packs/assets` and deletes the old ones, but a
 * browser can keep an old document alive (session restore, back/forward) and serve its entrypoint
 * chunk from its own `immutable` HTTP cache. Lazy chunks referenced by that old bundle were never
 * cached, so they go to the network and get a 404 - the page silently loses whatever they render
 * (main menu, dynamic filters, CKEditor, ...).
 *
 * When a chunk is provably gone from the server the only cure is a reload, which fetches fresh
 * HTML. A network failure looks the same to `import()` but must NOT reload, otherwise an offline
 * user is thrown onto the browser error page - hence the HEAD probe.
 *
 * Deliberately does not call `event.preventDefault()` on `vite:preloadError`: Vite then resolves
 * the import promise with `undefined` and every `const { x } = await import(...)` call site fails
 * on destructuring instead. This module never filters the rejection; `easy_hosting_services`
 * suppresses only the report whose recovery requested a reload.
 */

export const RELOAD_GUARD_KEY = "easy.stalePacksReloadedAt";
export const RELOAD_GUARD_WINDOW_MS = 60_000;
export const PROBE_TIMEOUT_MS = 5_000;

export const STALE_PACKS_RECOVERY_EVENT = "easy:stalePacksRecovery";
export const STALE_PACKS_RECOVERY_ATTEMPT_EVENT = "easy:stalePacksRecoveryAttempt";

/** Identity-only coordination for optional reporters; never include this in diagnostics or metadata. */
export interface StalePacksRecoveryAttempt {
  originalError: Error;
  recovery: Promise<boolean>;
}

type RecoveryBlocker = "offline" | "unsaved_changes" | "unsaved_state_unavailable" | "unknown_beforeunload_handler";

export interface StalePacksRecoveryDecision {
  reason:
    | RecoveryBlocker
    | "invalid_url"
    | "reload_guard_active"
    | "storage_unavailable"
    | "probe_started"
    | "probe_status_not_missing"
    | "probe_network_error"
    | "reload_requested";
  pathname?: string;
  status?: number;
}

const DYNAMIC_IMPORT_MESSAGE_PATTERN = /(?:Failed to fetch|error loading) dynamically imported module/i;

/** Statuses that mean "the server does not have this file", as opposed to "cannot reach server". */
export const MISSING_CHUNK_STATUSES = [403, 404];

/**
 * `Failed to fetch dynamically imported module: <url>` - Chromium/WebKit
 * `error loading dynamically imported module: <url>` - Firefox
 * `Unable to preload CSS for <url>` - Vite's own preload helper
 */
const CHUNK_URL_PATTERNS = [
  new RegExp(`${DYNAMIC_IMPORT_MESSAGE_PATTERN.source}:?\\s*(\\S+)`, "i"),
  /Unable to preload CSS for\s*(\S+)/i,
];

interface VitePreloadErrorEvent extends Event {
  payload?: unknown;
}

const getErrorMessage = (payload: unknown): string => {
  if (payload instanceof Error) return payload.message;
  if (typeof payload === "string") return payload;
  return "";
};

/**
 * Returns the failed chunk URL, or null when it cannot be trusted. The URL comes out of an error
 * message, so it is validated as same-origin before we ever fetch it.
 */
export const extractChunkUrl = (payload: unknown): string | null => {
  const message = getErrorMessage(payload);

  for (const pattern of CHUNK_URL_PATTERNS) {
    const match = message.match(pattern);
    if (match?.[1]) {
      const safeUrl = getSafeUrl(match[1].replace(/[.,;)\]]+$/, ""));
      return safeUrl && new URL(safeUrl).origin === window.location.origin ? safeUrl : null;
    }
  }

  return null;
};

const readLastReloadAt = (): number => {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  } catch {
    return 0;
  }
};

/**
 * Claims the right to reload. Returns false when sessionStorage is unavailable - without a working
 * guard a reload could loop forever, so we rather leave the page broken than spin it.
 */
const claimReload = (): boolean => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

const isReloadGuardActive = (): boolean => Date.now() - readLastReloadAt() < RELOAD_GUARD_WINDOW_MS;

const probeChunkStatus = async (url: string): Promise<number | null> => {
  const controller = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>(resolve => {
      timeoutId = setTimeout(() => {
        // Settle before aborting, even if fetch ignores abort or produces a late response.
        resolve(null);
        controller.abort();
      }, PROBE_TIMEOUT_MS);
    });
    const probe = fetch(url, { method: "HEAD", cache: "no-store", signal: controller.signal }).then(
      response => response.status
    );
    return await Promise.race([probe, timeout]);
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const getRecoveryBlocker = (): RecoveryBlocker | null => {
  if (window.navigator.onLine === false) return "offline";
  try {
    if (warnLeavingUnsaved.hasUnsavedChanges()) return "unsaved_changes";
  } catch {
    // A failing registered callback cannot establish that navigation is safe.
    return "unsaved_state_unavailable";
  }
  if (typeof window.onbeforeunload === "function" && !warnLeavingUnsaved.ownsBeforeUnloadHandler()) {
    return "unknown_beforeunload_handler";
  }
  return null;
};

/**
 * Reloads the page when the failed chunk is provably missing on the server.
 *
 * @returns true when a reload was triggered.
 */
export const recoverFromStalePacks = async (payload: unknown): Promise<boolean> => {
  const url = extractChunkUrl(payload);
  const reportDecisions = DYNAMIC_IMPORT_MESSAGE_PATTERN.test(getErrorMessage(payload));
  const report = (reason: StalePacksRecoveryDecision["reason"], status?: number): void => {
    if (!reportDecisions) return;

    const detail: StalePacksRecoveryDecision = { reason };
    if (url) detail.pathname = new URL(url).pathname;
    if (status !== undefined) detail.status = status;
    window.dispatchEvent(new CustomEvent<StalePacksRecoveryDecision>(STALE_PACKS_RECOVERY_EVENT, { detail }));
  };

  if (!url) {
    report("invalid_url");
    return false;
  }

  const blocker = getRecoveryBlocker();
  if (blocker) {
    report(blocker);
    return false;
  }

  if (isReloadGuardActive()) {
    report("reload_guard_active");
    return false;
  }

  report("probe_started");
  const status = await probeChunkStatus(url);
  if (status === null) {
    report("probe_network_error");
    return false;
  }
  if (!MISSING_CHUNK_STATUSES.includes(status)) {
    report("probe_status_not_missing", status);
    return false;
  }

  // Editing, handler ownership and connectivity can change while HEAD is in flight.
  const finalBlocker = getRecoveryBlocker();
  if (finalBlocker) {
    report(finalBlocker, status);
    return false;
  }

  // A stale document typically fails several chunks in the same tick, so re-check the guard after
  // the probe. No await or diagnostic event separates the final check from the successful claim.
  if (isReloadGuardActive()) {
    report("reload_guard_active", status);
    return false;
  }
  if (!claimReload()) {
    report("storage_unavailable", status);
    return false;
  }

  // Request navigation before notifying observers so they cannot invalidate the final safety check.
  window.location.reload();
  // This event describes a reload request, not proof of successful recovery.
  report("reload_requested", status);

  return true;
};

export const registerStalePacksRecovery = (): void => {
  window.addEventListener("vite:preloadError", event => {
    const originalError = (event as VitePreloadErrorEvent).payload;
    const recovery = recoverFromStalePacks(originalError);
    if (originalError instanceof Error && DYNAMIC_IMPORT_MESSAGE_PATTERN.test(originalError.message)) {
      // Publish synchronously, before Vite rethrows the unchanged payload, including immediate exits.
      window.dispatchEvent(
        new CustomEvent<StalePacksRecoveryAttempt>(STALE_PACKS_RECOVERY_ATTEMPT_EVENT, {
          detail: { originalError, recovery },
        })
      );
    }
  });
};
